// VOICE-1 (docs/specs/voice-1.md): the signed-in user's voice print — status and delete.
// Making one happens from a recording ("This is me" under Speakers), where the user picks
// their own voice. The nav entry exists only when the feature is on (App.tsx).
import type { VoicePrint } from '@scribe/core/types';
import { useFeatureEnabled } from '../lib/features';
import { useDeleteVoicePrint, useMyVoicePrint } from '../lib/voicePrints';

const FAILURE_TEXT: Record<string, string> = {
  not_enough_speech:
    'That speaker didn’t talk long enough. Pick a recording where you speak for at least 10 seconds.',
  audio_unavailable: 'That recording’s audio couldn’t be read. Try another recording.',
  error: 'Something went wrong. Try again from another recording.',
};

function statusText(print: VoicePrint): string {
  switch (print.status) {
    case 'none':
      return 'You don’t have a voice print.';
    case 'pending':
      return 'Making your voice print. This takes a few minutes.';
    case 'ready':
      return `Your voice print is ready (from ${Math.round(print.speech_seconds ?? 0)} seconds of speech). New recordings name you automatically.`;
    case 'failed':
      return FAILURE_TEXT[print.failure_reason ?? 'error'] ?? FAILURE_TEXT.error!;
  }
}

export function VoicePrintScreen() {
  const enabled = useFeatureEnabled('voice_prints');
  const print = useMyVoicePrint(enabled);
  const remove = useDeleteVoicePrint();

  if (!enabled) {
    return (
      <div className="card">
        <h2>Voice print</h2>
        <p className="muted">Voice prints aren’t switched on.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Voice print</h2>
      <p className="muted">
        Scribe can name you automatically in new recordings by recognising your voice. To make a
        voice print, open a recording you spoke in, find yourself under Speakers, and choose{' '}
        <strong>This is me</strong>.
      </p>
      {print.isLoading && <p className="muted">Loading…</p>}
      {print.error && <p className="error">Couldn’t load your voice print.</p>}
      {print.data && (
        <>
          <p role="status">{statusText(print.data)}</p>
          {print.data.status !== 'none' && (
            <button
              type="button"
              className="danger sm"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? 'Deleting…' : 'Delete my voice print'}
            </button>
          )}
          {remove.error && <p className="error">Couldn’t delete it. Try again.</p>}
        </>
      )}
    </div>
  );
}
